import { setup, assign, spawnChild, sendTo, fromCallback, fromPromise } from "xstate";
import { evaluationsMachineLogicRef } from "./evaluationsMachineLogicRef";

const routes = {
    //contracts: "/contracts",
    evaluations: "/evaluations",
};

const rootMachine = setup({
    actors: {
        // Child machine to handle navigation and url paths
        navigate: fromCallback(({ sendBack, receive }) => {
            receive((event) => {
                const { navigate, location } = event;

                // Navigate directly to the current location of the user and attach all search queries
                // Child machines will handle query based navigation
                navigate(`${location.pathname}${location.search}`, { replace: true });
                sendBack({
                    type: "locationChanged",
                    currentLocation: location.pathname // Send back the current location to be used in the route guards 
                });
            });
        }),
        // Evaluations root machine is loaded via dynamic import when entering /evaluations (see nested "/evaluations" states below).
    },
    guards: {
        isAuthenticated: ({ context }) => {
            // Only proceed if userData is not null
            return context.userData !== null;
        },
        // Route guards - Only proceed if the location matches the route for the state
        isContractsModule: ({ event }) => {
            return event.currentLocation === routes.contracts;
        },
        isEvaluationsModule: ({ event }) => {
            return event.currentLocation === routes.evaluations;
        }
    },
}).createMachine(
    {
        id: "root",
        // When the machine is created a navigation child machine is created within the context of the root machine
        entry: [
            spawnChild("navigate", { id: "navigate" }),
        ],
        // Initial state, always default to unknown
        initial: "unknown",
        // All the data variables for the machine, its screens and its states
        context: ({ input }) => ({
            userData: input?.userData || null,
            navigateContext: input?.navigateContext || null,
            locationContext: input?.locationContext || null,
            searchParamsContext: input?.searchParamsContext || null,
        }),
        states: {
            unknown: {
                // Immediately go to authenticated if userData provided, this verification happens on the specified guard
                always: [
                    { target: "authenticated", guard: "isAuthenticated" },
                    // If the guard for the authenticated state isn't passed it will default to the unauthenticated state
                    { target: "unauthenticated" }
                ],
            },
            authenticated: {
                // Like in the main machine, default to unkown within this state
                initial: "unknown",
                states: {
                    unknown: {
                        always: {
                            target: "loadChildMachine"
                        },
                    },
                    loadChildMachine: {
                        entry: [
                            sendTo("navigate", ({ context }) => {
                                return {
                                    type: "locationChanged",
                                    navigate: context.navigateContext,
                                    location: context.locationContext,
                                    searchParams: context.searchParamsContext,
                                }
                            }),
                        ],
                    },
                    idle: {
                    },
                    // "/contracts": {
                    //     invoke: {
                    //         src: "contractsMachine",
                    //         id: "contractsMachine",
                    //         input: ({ context }) => ({...context})
                    //     },
                    // },
                    "/evaluations": {
                        exit: [
                            () => {
                                evaluationsMachineLogicRef.current = null;
                            },
                        ],
                        initial: "importing",
                        states: {
                            importing: {
                                invoke: {
                                    id: "evaluationsModuleLoader",
                                    src: fromPromise(() =>
                                        import(
                                            "../../components/Evaluations/EvaluationsRootMachine/EvaluationsRootMachine"
                                        ).then((m) => m.default)
                                    ),
                                    onDone: {
                                        target: "ready",
                                        actions: [
                                            ({ event }) => {
                                                evaluationsMachineLogicRef.current = event.output;
                                            },
                                        ],
                                    },
                                    onError: {
                                        target: "#root.authenticated.idle",
                                    },
                                },
                            },
                            ready: {
                                invoke: {
                                    id: "evaluationsMachine",
                                    get src() {
                                        return evaluationsMachineLogicRef.current;
                                    },
                                    input: ({ context }) => ({ ...context }),
                                },
                            },
                        },
                    },
                },
                on: {
                    // All the states use a route verification guard
                    locationChanged: [
                        // {
                        //     // A dot is needed before the state that is being targueted since it's a state within a state
                        //     // 'authenticated' can be targueted directly but 'contracts' can't
                        //     // This rule applies for all the locations
                        //     target: "./contracts",
                        //     guard: "isContractsModule"
                        // },
                        {
                            target: "./evaluations",
                            guard: "isEvaluationsModule"
                        },
                        // Fallback to idle state if route is not specified or not found
                        {
                            target: ".idle",
                            guard: "isAuthenticated"
                        },
                    ],
                    updateLocation: {
                        target: ".loadChildMachine",
                        input: ({ event }) => ({...event}),
                        actions: [
                            assign({
                                locationContext: ({ event }) => event?.locationContext ?? null,
                                searchParamsContext: ({ event }) => event?.searchParamsContext ?? null,
                            }),
                        ]
                    },
                    unauthenticate: {
                        target: "unauthenticated"
                    },
                },
            },
            unauthenticated: {
                on: {
                    authenticate: {
                        target: "authenticated",
                        actions: [
                            assign({
                                userData: ({ event }) => event.userData,
                                navigateContext: ({ event }) => event.navigateContext,
                                locationContext: ({ event }) => event.locationContext,
                                searchParamsContext: ({ event }) => event.searchParamsContext,
                            }),
                        ]
                    },
                },
            },
        },
    },
);

export default rootMachine;